import React, { useEffect, useState } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { User } from '../types';
import { Button } from './ui';
interface BalanceEditorProps {
  user: User;
  /** When provided, an Edit affordance is shown (managers/admins only). */
  onUpdateBalance?: (userId: string, ptoTotal: number, ptoUsed: number) => void;
}
export function BalanceEditor({
  user,
  onUpdateBalance
}: BalanceEditorProps) {
  const hasBalance = typeof user.ptoTotal === 'number';
  const [isEditing, setIsEditing] = useState(false);
  const [total, setTotal] = useState<number>(user.ptoTotal || 0);
  const [used, setUsed] = useState<number>(user.ptoUsed || 0);
  const [error, setError] = useState<string | null>(null);
  // Reset local state whenever the user changes or edit mode opens
  useEffect(() => {
    setTotal(user.ptoTotal || 0);
    setUsed(user.ptoUsed || 0);
    setError(null);
  }, [user, isEditing]);
  // Loan Officers have no balance to draw from
  if (!hasBalance) {
    return <div className="bg-gray-50 border border-gray-200 rounded-md p-4 flex items-center justify-between text-sm" data-id="element-205">
        <span className="text-gray-500" data-id="element-206">PTO Balance</span>
        <span className="font-medium text-gray-400 italic" data-id="element-207">No PTO balance</span>
      </div>;
  }
  const remaining = (user.ptoTotal || 0) - (user.ptoUsed || 0);
  const handleSave = () => {
    if (total < 0 || used < 0) {
      setError('Values cannot be negative.');
      return;
    }
    if (used > total) {
      setError('Days used cannot exceed the total allowance.');
      return;
    }
    onUpdateBalance?.(user.id, total, used);
    setIsEditing(false);
  };
  if (isEditing) {
    return <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-3" data-id="element-208">
        <div className="flex items-center justify-between" data-id="element-209">
          <span className="text-sm font-medium text-gray-700" data-id="element-210">
            Edit PTO Balance
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-211">
          <div className="space-y-1" data-id="element-212">
            <label htmlFor={`pto-total-${user.id}`} className="block text-xs font-medium text-gray-500" data-id="element-213">
              Total Allowance (days)
            </label>
            <input id={`pto-total-${user.id}`} type="number" min={0} value={total} onChange={e => setTotal(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-[#0176d3] focus:border-[#0176d3] text-sm" data-id="element-214" />
          </div>
          <div className="space-y-1" data-id="element-215">
            <label htmlFor={`pto-used-${user.id}`} className="block text-xs font-medium text-gray-500" data-id="element-216">
              Days Used
            </label>
            <input id={`pto-used-${user.id}`} type="number" min={0} value={used} onChange={e => setUsed(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-[#0176d3] focus:border-[#0176d3] text-sm" data-id="element-217" />
          </div>
        </div>
        <p className="text-xs text-gray-500" data-id="element-218">
          Remaining will be {Math.max(0, total - used)} days.
        </p>
        {error && <p className="text-xs text-red-600" data-id="element-219">{error}</p>}
        <div className="flex justify-end gap-2 pt-1" data-id="element-220">
          <Button variant="neutral" className="px-3 py-1.5" onClick={() => setIsEditing(false)} data-id="element-221">
            <X className="w-4 h-4 mr-1" data-id="element-222" />
            Cancel
          </Button>
          <Button variant="brand" className="px-3 py-1.5" onClick={handleSave} data-id="element-223">
            <Check className="w-4 h-4 mr-1" data-id="element-224" />
            Save
          </Button>
        </div>
      </div>;
  }
  return <div className="bg-gray-50 border border-gray-200 rounded-md p-4 flex items-center justify-between text-sm gap-3" data-id="element-225">
      <span className="text-gray-500" data-id="element-226">PTO Balance</span>
      <div className="flex items-center gap-3" data-id="element-227">
        <span className="font-medium text-gray-900" data-id="element-228">
          {user.ptoUsed || 0} used
          <span className="text-gray-400 mx-2" data-id="element-229">·</span>
          {remaining} remaining
          <span className="text-gray-400 mx-2" data-id="element-230">·</span>
          {user.ptoTotal} total
        </span>
        {onUpdateBalance && <button type="button" onClick={() => setIsEditing(true)} aria-label={`Edit PTO balance for ${user.name}`} className="inline-flex items-center text-[#0176d3] hover:text-[#014486] focus:outline-none focus:ring-2 focus:ring-[#0176d3] rounded px-1.5 py-1 text-xs font-medium" data-id="element-231">
            <Pencil className="w-3.5 h-3.5 mr-1" data-id="element-232" />
            Edit
          </button>}
      </div>
    </div>;
}